import { useAuth } from "contexts/AuthContext";
import { useTranslation } from "contexts/i18nContext";
import {
  BadgeCheck,
  CalendarClock,
  FileStack,
  Gavel,
  Inbox,
  Landmark,
  ListChecks,
  RefreshCw,
  Scale,
  Search,
} from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { StatusChip } from "components/StatusChip";
import {
  DashboardStageKey,
  fetchAdminUnitName,
  fetchProsecutorDisciplineDashboard,
  ProsecutorDisciplineDashboard,
} from "services/dashboardService";
import { ROLES } from "utils/roles";
import {
  CountBars,
  CountUp,
  percent,
  Slice,
  StageDonut,
  TrendChart,
} from "./fragment/DashboardCharts";

type RangeKey = "all" | "7" | "30" | "90" | "year" | "custom";

const STAGE_COLORS: Record<DashboardStageKey, string> = {
  received: "#f59e0b",
  investigation: "#ea580c",
  council: "#991b1b",
  appeal: "#374151",
  closed: "#16a34a",
  other: "#d1d5db",
};

// The stage each role mainly works on, highlighted as "your queue".
const QUEUE_BY_ROLE: [string, DashboardStageKey][] = [
  [ROLES.INSPECTION_COORDINATOR, "received"],
  [ROLES.INSPECTION_DIRECTOR, "received"],
  [ROLES.INSPECTION_REGISTRAR, "council"],
  [ROLES.REGIONAL_COUNCIL_OFFICE_HEAD, "council"],
  [ROLES.ZONAL_COUNCIL_OFFICE_HEAD, "council"],
  [ROLES.REGIONAL_COUNCIL_OFFICE_WRITER, "council"],
  [ROLES.ZONAL_COUNCIL_OFFICE_WRITER, "council"],
  [ROLES.INSPECTION_PROSECUTOR, "investigation"],
];

const isoDay = (d: Date) => {
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

const rangeDates = (key: RangeKey): { from?: string; to?: string } => {
  const today = new Date();
  if (key === "all" || key === "custom") return {};
  if (key === "year") {
    return { from: `${today.getFullYear()}-01-01`, to: isoDay(today) };
  }
  const start = new Date(today);
  start.setDate(today.getDate() - Number(key) + 1);
  return { from: isoDay(start), to: isoDay(today) };
};

const formatDate = (value: string | null | undefined) =>
  value
    ? new Date(value).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "—";

const Card: React.FC<{
  title: React.ReactNode;
  action?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, action, children }) => (
  <section className="flex flex-col rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
    <div className="mb-4 flex items-center justify-between gap-3">
      <h2 className="text-base font-semibold text-gray-900">{title}</h2>
      {action}
    </div>
    <div className="flex-1">{children}</div>
  </section>
);

const DashboardPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();

  const [rangeKey, setRangeKey] = useState<RangeKey>("all");
  const [custom, setCustom] = useState({ from: "", to: "" });
  const [data, setData] = useState<ProsecutorDisciplineDashboard | null>(null);
  const [unitName, setUnitName] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<"forbidden" | "failed" | null>(null);

  const range = useMemo(
    () =>
      rangeKey === "custom"
        ? { from: custom.from || undefined, to: custom.to || undefined }
        : rangeDates(rangeKey),
    [rangeKey, custom],
  );

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    fetchProsecutorDisciplineDashboard(range)
      .then(setData)
      .catch((err) => {
        setData(null);
        setError(err?.response?.status === 403 ? "forbidden" : "failed");
      })
      .finally(() => setLoading(false));
  }, [range]);

  useEffect(() => {
    load();
  }, [load]);

  const scopeType = data?.scope.type;
  useEffect(() => {
    if (scopeType !== "admin_unit" || !user?.admin_unit) return;
    fetchAdminUnitName(user.admin_unit)
      .then(setUnitName)
      .catch(() => setUnitName(""));
  }, [scopeType, user?.admin_unit]);

  const queue = QUEUE_BY_ROLE.find(([role]) => user?.roles?.includes(role))?.[1];

  const stageLabels: Record<DashboardStageKey, string> = {
    received: t("pdStageReceived"),
    investigation: t("pdStageInvestigation"),
    council: t("pdStageCouncil"),
    appeal: t("pdStageAppeal"),
    closed: t("pdStageClosed"),
    other: t("pdStageOther"),
  };

  const rangeOptions: [RangeKey, string][] = [
    ["all", t("pdRangeAll")],
    ["7", t("pdRange7")],
    ["30", t("pdRange30")],
    ["90", t("pdRange90")],
    ["year", t("pdRangeYear")],
    ["custom", t("pdRangeCustom")],
  ];

  const header = (
    <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t("pdDashboardTitle")}</h1>
        <p className="mt-1 text-sm text-gray-600">
          {t("pdWelcomeBack")},{" "}
          <span className="font-semibold text-gray-900">
            {user?.full_name || user?.username}
          </span>
          .{" "}
          {scopeType === "assigned"
            ? t("pdOverviewAssigned")
            : scopeType === "all"
              ? t("pdOverviewAll")
              : t("pdOverviewUnit")}
        </p>
        {(scopeType === "assigned" || scopeType === "all" || unitName) && (
          <span className="mt-2 inline-flex items-center gap-2 rounded-full bg-red-50 px-3 py-1 text-xs font-medium text-red-800 ring-1 ring-red-200">
            <Landmark size={14} />
            {scopeType === "assigned"
              ? t("pdScopeAssigned")
              : scopeType === "all"
                ? t("pdScopeAll")
                : `${t("pdScopeUnit")}: ${unitName}`}
          </span>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={rangeKey}
          onChange={(e) => setRangeKey(e.target.value as RangeKey)}
          className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-red-800"
        >
          {rangeOptions.map(([key, label]) => (
            <option key={key} value={key}>
              {label}
            </option>
          ))}
        </select>
        {rangeKey === "custom" && (
          <>
            <label className="flex items-center gap-1 text-sm text-gray-600">
              {t("pdFrom")}
              <input
                type="date"
                value={custom.from}
                onChange={(e) => setCustom((c) => ({ ...c, from: e.target.value }))}
                className="rounded-lg border border-gray-300 px-2 py-1.5 text-sm"
              />
            </label>
            <label className="flex items-center gap-1 text-sm text-gray-600">
              {t("pdTo")}
              <input
                type="date"
                value={custom.to}
                onChange={(e) => setCustom((c) => ({ ...c, to: e.target.value }))}
                className="rounded-lg border border-gray-300 px-2 py-1.5 text-sm"
              />
            </label>
          </>
        )}
        <button
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-lg bg-red-800 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60"
        >
          <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
          {t("pdRefresh")}
        </button>
      </div>
    </div>
  );

  if (error) {
    return (
      <div className="space-y-6">
        {header}
        <div
          className={`rounded-2xl border p-8 text-center ${
            error === "forbidden"
              ? "border-gray-200 bg-white text-gray-600"
              : "border-red-200 bg-red-50 text-red-800"
          }`}
        >
          {error === "forbidden" ? t("pdNoRoleDashboard") : t("pdLoadFailed")}
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="space-y-6">
        {header}
        <div className="flex h-64 items-center justify-center text-gray-500">
          <div className="mr-3 h-6 w-6 animate-spin rounded-full border-2 border-red-800 border-t-transparent" />
          {t("pdLoading")}
        </div>
      </div>
    );
  }

  const { totals } = data;
  const change =
    totals.previous_total && totals.previous_total > 0
      ? ((totals.total - totals.previous_total) / totals.previous_total) * 100
      : null;

  const stageCards: { key: DashboardStageKey; hint: string; icon: React.ElementType }[] = [
    { key: "received", hint: t("pdStageReceivedHint"), icon: Inbox },
    { key: "investigation", hint: t("pdStageInvestigationHint"), icon: Search },
    { key: "council", hint: t("pdStageCouncilHint"), icon: Gavel },
    { key: "closed", hint: t("pdStageClosedHint"), icon: BadgeCheck },
  ];

  const appealChips = [
    { label: t("menuAppealedComplaint"), value: data.appeals.appeal_requests, dot: "bg-red-800", badge: "bg-red-50 text-red-800", path: "/appeal-request" },
    { label: t("menuAccusedAppeal"), value: data.appeals.accused_appeals, dot: "bg-orange-600", badge: "bg-orange-50 text-orange-700", path: "/accused-complaint" },
    { label: t("menuCourtAppeal"), value: data.appeals.court_appeals, dot: "bg-gray-700", badge: "bg-gray-100 text-gray-800", path: "/court-appeal" },
    { label: t("pdSummons"), value: data.appeals.summons, dot: "bg-amber-500", badge: "bg-amber-50 text-amber-700", path: "/complaint-request" },
  ];

  const slices: Slice[] = (
    ["received", "investigation", "council", "appeal", "closed", "other"] as DashboardStageKey[]
  )
    .map((key) => ({ key, label: stageLabels[key], value: totals[key], color: STAGE_COLORS[key] }))
    .filter((slice) => slice.key !== "other" || slice.value > 0);

  const trendPoints = data.trend.map((p) => ({
    label: new Date(`${p.month}-01T00:00:00`).toLocaleString(undefined, { month: "short" }),
    received: p.received,
    closed: p.closed,
  }));

  const viewAll = (path: string) => (
    <Link to={path} className="text-sm font-medium text-red-800 hover:underline">
      {t("pdViewAll")}
    </Link>
  );

  return (
    <div className={`space-y-6 ${loading ? "opacity-70" : ""}`}>
      {header}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
        <Link
          to="/complaint-request"
          className="rounded-2xl bg-red-800 p-5 text-white shadow-sm transition hover:bg-red-700"
        >
          <div className="flex items-start gap-3">
            <span className="rounded-full bg-white/15 p-2.5">
              <Scale size={20} />
            </span>
            <div>
              <p className="text-sm text-red-100">{t("pdTotalCases")}</p>
              <p className="mt-1 text-3xl font-bold"><CountUp value={totals.total} /></p>
              <p className="text-xs text-red-100">{t("pdTotalCasesHint")}</p>
            </div>
          </div>
          {change !== null && (
            <p className="mt-4 text-xs text-red-100">
              {change >= 0 ? "↑" : "↓"} {Math.abs(change).toFixed(0)}% {t("pdFromPrevious")}
            </p>
          )}
        </Link>

        {stageCards.map(({ key, hint, icon: Icon }) => {
          const isQueue = key === queue;
          return (
            <Link
              key={key}
              to="/complaint-request"
              className={`rounded-2xl border bg-white p-5 shadow-sm transition hover:shadow-md ${
                isQueue ? "border-red-800 ring-1 ring-red-800" : "border-gray-200"
              }`}
            >
              <div className="flex items-start gap-3">
                <span
                  className="rounded-full p-2.5"
                  style={{ backgroundColor: `${STAGE_COLORS[key]}1a`, color: STAGE_COLORS[key] }}
                >
                  <Icon size={20} />
                </span>
                <div className="min-w-0">
                  <p className="flex flex-wrap items-center gap-2 text-sm text-gray-600">
                    {stageLabels[key]}
                    {isQueue && (
                      <span className="rounded-full bg-red-800 px-2 py-0.5 text-[10px] font-semibold uppercase text-white">
                        {t("pdYourQueue")}
                      </span>
                    )}
                  </p>
                  <p className="mt-1 text-3xl font-bold text-gray-900"><CountUp value={totals[key]} /></p>
                  <p className="text-xs text-gray-500">{hint}</p>
                </div>
              </div>
              <p className="mt-4 text-xs text-gray-500">
                {percent(totals[key], totals.total)} {t("pdOfTotal")}
              </p>
            </Link>
          );
        })}
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {appealChips.map((chip) => (
          <Link
            key={chip.label}
            to={chip.path}
            className="flex items-center justify-between rounded-2xl border border-gray-200 bg-white px-5 py-4 shadow-sm transition hover:shadow-md"
          >
            <span className="flex items-center gap-3">
              <span className={`h-2.5 w-2.5 rounded-full ${chip.dot}`} />
              <span className="font-medium text-gray-800">{chip.label}</span>
            </span>
            <span className={`rounded-lg px-3 py-1 text-sm font-semibold ${chip.badge}`}>
              <CountUp value={chip.value} />
            </span>
          </Link>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <Card title={t("pdCasesByStage")} action={viewAll("/complaint-request")}>
          <StageDonut slices={slices} total={totals.total} centerLabel={t("pdTotalCases")} />
        </Card>
        <Card
          title={
            <>
              {t("pdCaseTrend")}{" "}
              <span className="font-normal text-gray-500">{t("pdLast6Months")}</span>
            </>
          }
        >
          <TrendChart
            points={trendPoints}
            receivedLabel={t("pdStageReceived")}
            closedLabel={t("pdStageClosed")}
          />
        </Card>
        <Card title={t("pdByOffence")}>
          <CountBars rows={data.offence_types} emptyText={t("pdNoData")} />
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <Card title={t("pdUpcomingHearings")}>
          {data.upcoming_hearings.length ? (
            <ul className="divide-y divide-gray-100">
              {data.upcoming_hearings.map((h) => (
                <li key={`${h.complaint_id}-${h.hearing_date}`}>
                  <Link
                    to={`/complaint-request/${h.complaint_id}`}
                    className="flex items-center gap-3 py-3 hover:bg-gray-50"
                  >
                    <span className="rounded-lg bg-red-50 p-2 text-red-800">
                      <CalendarClock size={18} />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-gray-900">
                        {h.accused_prosecutor_name || "—"}
                      </span>
                      <span className="block text-xs text-gray-500">
                        {h.application_number || "—"}
                      </span>
                    </span>
                    <span className="text-xs font-semibold text-red-800">
                      {formatDate(h.hearing_date)}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <p className="py-8 text-center text-sm text-gray-500">{t("pdNoHearings")}</p>
          )}
        </Card>

        <Card title={t("pdRecentCases")} action={viewAll("/complaint-request")}>
          {data.recent_cases.length ? (
            <ul className="divide-y divide-gray-100">
              {data.recent_cases.map((c) => (
                <li key={c.id}>
                  <Link
                    to={`/complaint-request/${c.id}`}
                    className="flex items-center gap-3 py-3 hover:bg-gray-50"
                  >
                    <span className="rounded-lg bg-gray-100 p-2 text-gray-700">
                      <FileStack size={18} />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-gray-900">
                        {c.accused_prosecutor_name || "—"}
                      </span>
                      <span className="block text-xs text-gray-500">
                        {c.application_number || "—"} · {formatDate(c.created_at)}
                      </span>
                    </span>
                    <span className="shrink-0 text-xs">
                      <StatusChip status={c.status} />
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <p className="py-8 text-center text-sm text-gray-500">{t("pdNoData")}</p>
          )}
        </Card>

        <Card
          title={
            <span className="flex items-center gap-2">
              <ListChecks size={18} className="text-red-800" />
              {t("pdByDecision")}
            </span>
          }
        >
          <CountBars rows={data.decision_types} emptyText={t("pdNoData")} />
        </Card>
      </div>
    </div>
  );
};

export default DashboardPage;
